import React, { useEffect, useState } from "react";
import { Box } from "@mui/material";
import { useFormik } from "formik";
import * as Yup from "yup";
import {
  EditableColumnDef,
  EditableTable,
} from "../../../../../../shared/components/table/inline-edit-table";
import { CustomSelect, CustomTextField } from "../../../../../../shared/components/inputfields/form-elements";
import { StyledButton } from "../../../../../../shared/components/buttons/custom-button";
import { ITcProductRawMaterialInformation } from "../../../../tc-models/tc-req-dto/tc-certified-product-details.req_dto";
import { useFormActions } from "../../../../../../shared/hooks/form-builder.hooks";
import { dtrackItService } from "../../../../tc-service/dtrackit.service";
import useNotification from "../../../../../../shared/hooks/useNotification";
import { useTransactionCertificateStore } from "../../../../../../store/transaction-certificates.store";
import { Utills } from "../../../../../../shared/app-configs/utilit";
 
interface RawMaterialsInfoProps {
  rawMaterialInfoData: ITcProductRawMaterialInformation[]; // Expecting an array
  setRawMaterialInfoData: React.Dispatch<
    React.SetStateAction<ITcProductRawMaterialInformation[]>
  >;
}
 
const RawMaterialInfo: React.FC<RawMaterialsInfoProps> = ({
  rawMaterialInfoData,
  setRawMaterialInfoData,
}) => {
  const normalizeData = (arr: ITcProductRawMaterialInformation[]) =>
    arr.map((item, index) => {
      const normalizedItem = {
        tcProductRawMaterialOrder: item.tcProductRawMaterialOrder ?? "",
        tcProductRawMaterialCode: item.tcProductRawMaterialCode ?? "",
        tcProductRawMaterialCodeDescription: item.tcProductRawMaterialCodeDescription ?? "",
        tcProductRawMaterialPercentage: item.tcProductRawMaterialPercentage ?? "",
        tcProductRawMaterialUserSpecificTerm: item.tcProductRawMaterialUserSpecificTerm ?? "",
        additionalProp1: item.additionalProp1 ?? {},
      };
      return normalizedItem;
    });  
  const [data, setData] = useState<ITcProductRawMaterialInformation[]>(
    normalizeData(rawMaterialInfoData)
  );
  const [tableData, setTableData] = useState<ITcProductRawMaterialInformation[]>(
    normalizeData(rawMaterialInfoData)
  );
  const { notify } = useNotification()
  const { isRestrictPopUpSave, setRestrictPopUpSave, setRawMtrlCodes } = useTransactionCertificateStore();
  const [rawMaterials, setRawMaterials] = useState([]);
  const [editingRowIndex, setEditingRowIndex] = useState<number>(-1);
  const { updateField } = useFormActions("certified-products");
  // Define Yup validation schema for each row
  const validationSchema = Yup.object().shape({
    tcProductRawMaterialCode: Yup.string().required("TC Standard is required"),
    tcProductRawMaterialPercentage: Yup.number()
      .typeError("Percentage must be a number")
      .positive("Percentage must be positive").max(100, "Percentage cannot exceed 100")
      .required("Percentage is required")
      .test(
        'is-decimal',
        'Percentage must have at most 5 decimal places',
        (value: any) => {
          if (value === undefined || value === null) return true;
          return /^[0-9]+(\.[0-9]{1,5})?$/.test(value.toString());
        }
      )
      .test(
        'percentage-equals-100',
        'percentage-equals-100',
        function (value: any) {
          if (editingRowIndex > -1) {
            let total = data.reduce((sum, item, index) => {
              if (index === editingRowIndex) return sum;
              return sum + parseFloat(item.tcProductRawMaterialPercentage);
            }, 0);
            // if ((total + value) > 100) {
            //   notify("Raw Material Percentage cannot be greater than 100", "error")
            //   return false
            // }
          }
          return true;

        }
      ),
  });
  const formik = useFormik<ITcProductRawMaterialInformation>({
    initialValues: {
      tcProductRawMaterialOrder: "",
      tcProductRawMaterialCode: "",
      tcProductRawMaterialCodeDescription: "",
      tcProductRawMaterialPercentage: "",
      tcProductRawMaterialUserSpecificTerm: "",
    },
    validationSchema,
    validateOnChange: true,
    validateOnBlur: true,
    onSubmit: () => {
      // Submission logic if needed
    },
  });
  
  useEffect(() => {
    const normalized = normalizeData(rawMaterialInfoData);
    setData(normalized);
    setTableData(normalized);

  }, [rawMaterialInfoData]);

  useEffect(() => {
    // Ensure all rows have the field
    const normalized = normalizeData(data);
    normalized.forEach((item: any, index) => {
      item.tcProductRawMaterialOrder = index + 1;
    });
    updateField("raw-material-info", normalized);
  }, [data]);
 
  useEffect(() => {
    const getRawMaterialCodes = async () => {
      try {
        const result: any = await dtrackItService.getRawMaterialCodes()
        const rawMaterials = result.data.map((t: any) => ({ value: t.code_1, label: `${t.description_1} (${t.code_1})` }))
        Utills.cl(`rawMaterials ${JSON.stringify(rawMaterials)}`)
        setRawMaterials(rawMaterials);
        setRawMtrlCodes(rawMaterials);
      } catch (err) {
        Utills.cl(`Error in Fetching Organic Farm Standard Code ${err}`)
      }
    };
    getRawMaterialCodes()
  }, [])

  const handleCellChange = (
    rowIndex: number,
    columnId: string,
    value: any
  ) => {
    formik.setFieldValue(columnId, value);
    setData((prevData) =>
      prevData.map((row, index) =>
        index === rowIndex ? { ...row, [columnId]: value } : row
      )
    );
  };

  const handleAddNewRow = () => {
    const newRow: ITcProductRawMaterialInformation = {
      tcProductRawMaterialOrder: "",
      tcProductRawMaterialCode: "",
      tcProductRawMaterialCodeDescription: "",
      tcProductRawMaterialPercentage: "",
      tcProductRawMaterialUserSpecificTerm: "",
    };
    setData((prevData) => [...prevData, newRow]);
    setEditingRowIndex(data.length);
    setRestrictPopUpSave(true);
    formik.setValues(newRow);
    formik.setTouched({});
  };

  const handleCancelEdit = async () => {
    // Restore data to the last saved state
    setData(normalizeData(tableData));
    setRestrictPopUpSave(false);
    // Reset editing state and form
    setEditingRowIndex(-1);
    formik.resetForm();
  };

  const handleSaveRow = () => {
    if (editingRowIndex === null || !formik.isValid) return;
    
    // Preserve existing row data and merge with form values
    const existingRow = data[editingRowIndex];
    const updatedRow = {
      ...existingRow, // Keep all existing fields (including additionalProp1, etc.)
      ...formik.values, // Override with form values
      tcProductRawMaterialUserSpecificTerm: formik.values.tcProductRawMaterialUserSpecificTerm ?? "",
       tcProductRawMaterialCodeDescription: existingRow.tcProductRawMaterialCodeDescription ?? "", // Preserve backend description
    };
    const newData = data.map((row, index) =>
      index === editingRowIndex ? updatedRow : row
    );
    
    setData(newData);
    setTableData(newData);
    setRawMaterialInfoData(newData); // Update parent with updated data
    setEditingRowIndex(-1);
    setRestrictPopUpSave(false);
    formik.resetForm();
  };

  const columns: EditableColumnDef<ITcProductRawMaterialInformation>[] = [
    {
      accessorKey: "tcProductRawMaterialCode",
      header: "Product Raw Material Code",
      Header: <span> <i style={{ color: 'red' }}>* </i>Product Raw Material Code</span>,
      renderEditCell: ({ value, onChange }) => (
        <CustomSelect
          label=""
          name="tcProductRawMaterialCode"
          value={value}
          options={rawMaterials}
          enableSearch={true}
          placeholder="Select Product Raw Material Code"
          onChange={(e) => {
            onChange(e.target.value)
          }}
        />
      ),
    },
    {
      accessorKey: "tcProductRawMaterialPercentage",
      header: "Product Raw Material Percentage",
      Header: <span> <i style={{ color: 'red' }}>* </i>Product Raw Material Percentage</span>,
      renderEditCell: ({ value, onChange }) => (
        <CustomTextField
          name="tcProductRawMaterialPercentage"
          value={value}
          inputProps={{ max: 100 }}
          type="number"
          onChange={(e) => {
            const newValue = e.target.value;
            if (!newValue || (Number(newValue) <= 100 && /^[0-9]*\.?[0-9]{0,5}$/.test(newValue))) { onChange(newValue) }
          }
          }
          placeholder="Enter value"
          sx={{ mb: 2 }}
        />
      ),
    },
    {
      accessorKey: "tcProductRawMaterialUserSpecificTerm",
      header: "Product Raw Material User Specific Term",
      Header: <span> <i style={{ color: 'red' }}>* </i>Product Raw Material User Specific Term</span>,
      renderEditCell: ({ value, onChange }) => (
        <CustomTextField
          name="tcProductRawMaterialUserSpecificTerm"
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder="Product Raw Material User Specific Term"
          sx={{ mb: 2 }}
        />
      ),
    },
  ];

  const handleEditRow = (rowIndex: number) => {
    setEditingRowIndex(rowIndex);
    setRestrictPopUpSave(true);
    const row = data[rowIndex];    
    formik.setValues({
      tcProductRawMaterialOrder: row.tcProductRawMaterialOrder ?? "",
      tcProductRawMaterialCode: row.tcProductRawMaterialCode ?? "",
      tcProductRawMaterialCodeDescription: row.tcProductRawMaterialCodeDescription ?? "",
      tcProductRawMaterialPercentage: row.tcProductRawMaterialPercentage ?? "",

      tcProductRawMaterialUserSpecificTerm: row.tcProductRawMaterialUserSpecificTerm ?? "",
    });
  };

  const handleDeleteRow = (rowIndex: number) => {
    const updatedData = data.filter((_, index) => index !== rowIndex);
    setData(updatedData);
    setTableData(updatedData);
    setRawMaterialInfoData(updatedData); 
  };
 
  return (
    <div>
      <Box
        component="main"
        sx={{ flexGrow: 1, p: 1, border: "2px solid rgba(128, 128, 128, .2)", my: 4 }}
      >
        <Box display="flex" mb={3} justifyContent="flex-end" width="100%">
          <StyledButton disabled={isRestrictPopUpSave} onClick={handleAddNewRow} sx={{ mb: 2 }}>
            Add Raw Material
          </StyledButton>
        </Box>
        <EditableTable
          data={data}
          columns={columns}
          onSaveRow={handleSaveRow}
          onCellChange={handleCellChange}
          editingRowIndex={editingRowIndex}
          onEditRow={handleEditRow}
          onCancelEdit={handleCancelEdit}
          onDeleteRow={handleDeleteRow}
        />
      </Box>
    </div>
  );
};

export default RawMaterialInfo;
